import { z } from 'zod';
import { userRoleSchema } from './auth.js';
import { tradeSchema } from './project.js';

/**
 * Project invitation. Previously called "owner invitation" because the first
 * version only supported inviting clients/proprietários; now it's the generic
 * invite flow for any role — PM, foreman, worker, client, broker.
 *
 * The table name in the DB is still `owner_invitations` for migration
 * continuity, but conceptually this is the invitations table.
 */
export const ownerInvitationSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  email: z.string().email(),
  role: userRoleSchema,
  /** Optional specialization — see tradeSchema in project.ts. */
  trade: z.string().nullable(),
  expiresAt: z.string(),
  acceptedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type OwnerInvitation = z.infer<typeof ownerInvitationSchema>;

/** Generic invitation alias — prefer this name for new code. */
export const invitationSchema = ownerInvitationSchema;
export type Invitation = OwnerInvitation;

export const createOwnerInvitationRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  role: userRoleSchema,
  trade: tradeSchema.nullable().optional(),
});
export type CreateOwnerInvitationRequest = z.infer<typeof createOwnerInvitationRequestSchema>;

/** Generic alias. */
export const createInvitationRequestSchema = createOwnerInvitationRequestSchema;
export type CreateInvitationRequest = CreateOwnerInvitationRequest;

export const createOwnerInvitationResponseSchema = z.object({
  invitation: ownerInvitationSchema,
  acceptUrl: z.string().url(),
});
export type CreateOwnerInvitationResponse = z.infer<typeof createOwnerInvitationResponseSchema>;

export const acceptInvitationPreviewSchema = z.object({
  projectName: z.string(),
  email: z.string().email(),
  role: userRoleSchema,
  trade: z.string().nullable(),
  expiresAt: z.string(),
  alreadyAccepted: z.boolean(),
  userExists: z.boolean(),
  // W144 · 2026-05-27 — TODO(backend): expose once owner_invitations has invited_by_user_id column + users JOIN
  invitedByName: z.string().nullable().optional(),
  invitedByWhatsapp: z.string().nullable().optional(),
});
export type AcceptInvitationPreview = z.infer<typeof acceptInvitationPreviewSchema>;

export const acceptInvitationRequestSchema = z.object({
  token: z.string().min(10),
  // Required only when the invitee doesn't yet have an account:
  fullName: z.string().trim().min(1).max(120).optional(),
  phone: z
    .string()
    .trim()
    .regex(/^\+?[0-9 ()-]{7,20}$/, 'Invalid phone')
    .optional(),
  password: z
    .string()
    .min(8)
    .max(200)
    .regex(/[A-Za-z]/)
    .regex(/[0-9]/)
    .optional(),
});
export type AcceptInvitationRequest = z.infer<typeof acceptInvitationRequestSchema>;
